import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Typography
} from "@material-ui/core";

export const MtfPlotGenerator = ({
  sweptVariables,
  mtfPlots,
  onGenerate,
  isCreating,
  autoGenerate,
  onError
}) => {
  const [sweepVariable, setSweepVariable] = useState(null);
  const [fixedParameters, setFixedParameters] = useState(null); // Start as null to track initialization
  const [mtfType, setMtfType] = useState("tangential");
  const hasAutoGenerated = useRef(false);

  useEffect(() => {
    if (sweptVariables.length > 0 && fixedParameters === null) {
      const defaultSweepVar = sweptVariables[0];
      setSweepVariable(defaultSweepVar.name);

      const initialFixedParams = {};
      const otherVars = sweptVariables.filter(
        sv => sv.name !== defaultSweepVar.name
      );

      otherVars.forEach(sv => {
        const options = String(sv.values).split(/,|;|:/);
        if (options.length > 0) {
          initialFixedParams[sv.name] = parseFloat(options[0]);
        }
      });
      setFixedParameters(initialFixedParams);
    } else if (sweptVariables.length === 0 && fixedParameters === null) {
      setFixedParameters({});
    }
  }, [sweptVariables, fixedParameters]);

  useEffect(() => {
    if (sweepVariable && fixedParameters && fixedParameters[sweepVariable]) {
      const newFixedParams = { ...fixedParameters };
      delete newFixedParams[sweepVariable];
      setFixedParameters(newFixedParams);
    }
  }, [sweepVariable, fixedParameters]);

  useEffect(() => {
    if (sweptVariables.length === 0) {
      setMtfType("both");
    }
  }, [sweptVariables]);

  useEffect(() => {
    if (
      fixedParameters !== null &&
      mtfType === "both" &&
      !hasAutoGenerated.current &&
      !isCreating &&
      autoGenerate
    ) {
      hasAutoGenerated.current = true;
      handleGenerateClick();
    }
  }, [autoGenerate]);

  const handleGenerateClick = () => {
    if (sweptVariables.length > 0) {
      if (!sweepVariable || fixedParameters === null) {
        onError("Plot generator state not yet initialized");
        return;
      }
      const sweepVarObj = sweptVariables.find(sv => sv.name === sweepVariable);
      if (!sweepVarObj || !sweepVarObj.location) {
        onError("Sweep variable location is not defined");
        return;
      }
      const exists = mtfPlots?.some(
        plot =>
          (plot.yName.toLowerCase() === mtfType ||
            (mtfType === "both" &&
              plot.yName.toLowerCase().includes("tangential/sagittal"))) &&
          sweptVariables.length > 0 &&
          plot.sliderName === sweepVariable
      );
      if (exists) {
        onError("An MTF plot with the selected parameters already exists");
        return;
      }

      const fixedParamsForApi = Object.entries(fixedParameters).map(
        ([name, value]) => {
          const fixedVarObj = sweptVariables.find(sv => sv.name === name);
          return { name, location: fixedVarObj?.location, value };
        }
      );

      const plotDefinition = {
        sweep_variable: { name: sweepVariable, location: sweepVarObj.location },
        fixed_parameters: fixedParamsForApi,
        mtf_type: mtfType
      };
      onGenerate(plotDefinition);
    } else {
      // This is the payload when no swept variables exist.
      const exists = mtfPlots?.some(
        plot =>
          plot.yName.toLowerCase() === mtfType ||
          (mtfType === "both" &&
            plot.yName.toLowerCase().includes("tangential/sagittal"))
      );
      if (exists) {
        onError("An MTF plot with the selected parameters already exists");
        return;
      }
      const plotDefinition = {
        mtf_type: mtfType
      };
      onGenerate(plotDefinition);
    }
  };

  // Don't render anything until the state is initialized.
  if (fixedParameters === null) {
    return null;
  }

  const otherVariables = sweptVariables.filter(sv => sv.name !== sweepVariable);

  return (
    <Grid
      container
      item
      xs={8}
      spacing={2}
      display="grid"
      justifyContent="center"
      alignItems="center"
      style={{
        padding: 16,
        border: "1px solid #ccc",
        borderRadius: 4
      }}
    >
      <Grid item xs={12}>
        <Typography variant="h6">Generate MTF Plot</Typography>
      </Grid>

      {sweptVariables.length > 0 && (
        <>
          {/* Sweep Variable Selection */}
          <Grid item xs={3}>
            <FormControl fullWidth>
              <InputLabel>Sweep Variable</InputLabel>
              <Select
                value={sweepVariable || ""}
                onChange={e => setSweepVariable(e.target.value)}
              >
                {sweptVariables.map(sv => (
                  <MenuItem key={sv.name} value={sv.name}>
                    {sv.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Fixed Parameters Selection */}
          {otherVariables.map(sv => {
            const options = String(sv.values)
              .split(/,|;|:/)
              .map(v => parseFloat(v));
            const selectedValue = fixedParameters[sv.name] || options[0];

            return (
              <Grid item xs={3} key={sv.name}>
                <FormControl fullWidth>
                  <InputLabel>{`Fixed ${sv.name}`}</InputLabel>
                  <Select
                    value={selectedValue}
                    onChange={e =>
                      setFixedParameters({
                        ...fixedParameters,
                        [sv.name]: e.target.value
                      })
                    }
                  >
                    {options.map(val => (
                      <MenuItem key={val} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            );
          })}
        </>
      )}

      {/* MTF Type Selection */}
      <Grid item xs={sweptVariables.length > 0 ? 3 : 9}>
        <FormControl fullWidth>
          <InputLabel>MTF Type</InputLabel>
          <Select value={mtfType} onChange={e => setMtfType(e.target.value)}>
            <MenuItem value="tangential">Tangential</MenuItem>
            <MenuItem value="sagittal">Sagittal</MenuItem>
            <MenuItem value="both">Both</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Generate Button */}
      <Grid item xs={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateClick}
          disabled={isCreating}
        >
          {isCreating ? <CircularProgress size={24} /> : "Generate Plot"}
        </Button>
      </Grid>
    </Grid>
  );
};
